// Sleep-timeline data pipeline: Oura CSV loading, per-year processing, and
// derived helpers. Split from SleepTimeline so that stays a view component.

import { parseISO, differenceInCalendarDays } from 'date-fns'
import logger from '@/lib/logger'
import { currentYear } from '@/lib/site'

export interface SleepData {
  date: Date
  hoursSlept?: number | null
  sleepScore: number | null
  deepSleep: number | null
  remSleep: number | null
  totalSleep: number | null
  restingHeartRate: number | null
  readinessScore: number | null
  activityScore: number | null
  steps: number | null
  hrv: number | null
  sleepDebt: number | null
  sleepEfficiency: number | null
}

// CSV row structure from Oura export
interface OuraCSVRow {
  day: string
  total_sleep_hours?: number
  daily_sleep_score?: number
  deep_pct?: number
  rem_pct?: number
  sleep_average_heart_rate?: number
  readiness_score?: number
  activity_score?: number
  activity_steps?: number
  sleep_average_hrv?: number
  sleep_debt_hours?: number
  sleep_efficiency?: number
}

// Visualization mode configuration - single source of truth
interface VisualizationModeConfig {
  key: keyof SleepData
  maxValue: number
  lowThreshold: number
  highThreshold: number
  lowerIsBetter: boolean
  format: (value: number) => string
}

export const VISUALIZATION_MODES: Record<string, VisualizationModeConfig> = {
  sleepScore: {
    key: 'sleepScore',
    maxValue: 100,
    lowThreshold: 70,
    highThreshold: 90,
    lowerIsBetter: false,
    format: (v) => `${Math.round(v)}/100`,
  },
  deepSleep: {
    key: 'deepSleep',
    maxValue: 40,
    lowThreshold: 15,
    highThreshold: 25,
    lowerIsBetter: false,
    format: (v) => `${v.toFixed(1)}%`,
  },
  remSleep: {
    key: 'remSleep',
    maxValue: 40,
    lowThreshold: 15,
    highThreshold: 25,
    lowerIsBetter: false,
    format: (v) => `${v.toFixed(1)}%`,
  },
  totalSleep: {
    key: 'totalSleep',
    maxValue: 12,
    lowThreshold: 6,
    highThreshold: 8,
    lowerIsBetter: false,
    format: (v) => `${v.toFixed(1)} hours`,
  },
  restingHeartRate: {
    key: 'restingHeartRate',
    maxValue: 100,
    lowThreshold: 60,
    highThreshold: 50,
    lowerIsBetter: true,
    format: (v) => `${Math.round(v)} bpm`,
  },
  readinessScore: {
    key: 'readinessScore',
    maxValue: 100,
    lowThreshold: 70,
    highThreshold: 90,
    lowerIsBetter: false,
    format: (v) => `${Math.round(v)}/100`,
  },
  activityScore: {
    key: 'activityScore',
    maxValue: 100,
    lowThreshold: 70,
    highThreshold: 90,
    lowerIsBetter: false,
    format: (v) => `${Math.round(v)}/100`,
  },
  steps: {
    key: 'steps',
    maxValue: 20000,
    lowThreshold: 7000,
    highThreshold: 10000,
    lowerIsBetter: false,
    format: (v) => `${Math.round(v).toLocaleString()} steps`,
  },
  hrv: {
    key: 'hrv',
    maxValue: 150,
    lowThreshold: 40,
    highThreshold: 60,
    lowerIsBetter: false,
    format: (v) => `${Math.round(v)} ms`,
  },
  sleepDebt: {
    key: 'sleepDebt',
    maxValue: 5,
    lowThreshold: 2,
    highThreshold: 0.5,
    lowerIsBetter: true,
    format: (v) => `${v.toFixed(1)} hours`,
  },
  sleepEfficiency: {
    key: 'sleepEfficiency',
    maxValue: 100,
    lowThreshold: 80,
    highThreshold: 90,
    lowerIsBetter: false,
    format: (v) => `${v.toFixed(1)}%`,
  },
}

export function getBarHeight(
  day: SleepData,
  mode: string,
  getValue: (day: SleepData) => number | null,
): string {
  const value = getValue(day)
  if (value === null || value === undefined || !isFinite(value)) {
    return '16px'
  }
  const config = VISUALIZATION_MODES[mode]
  const maxValue = config?.maxValue ?? 100
  const percentage = Math.min(100, (value / maxValue) * 100)
  return `${Math.max(5, percentage)}%`
}

// Load and parse CSV data - lazy-load papaparse to reduce initial bundle
export const loadCSVData = async (): Promise<OuraCSVRow[]> => {
  try {
    const response = await fetch('/sleep.csv')
    // A missing/erroring CSV returns an HTML error page, which Papa would
    // happily parse into rows of nonsense — fail loudly so the caller falls
    // back to generated data instead.
    if (!response.ok) {
      throw new Error(`sleep.csv fetch failed: ${response.status}`)
    }
    const csvText = await response.text()

    // Dynamic import papaparse only when needed
    const Papa = (await import('papaparse')).default

    return new Promise((resolve, reject) => {
      Papa.parse<OuraCSVRow>(csvText, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          logger.debug({ rowCount: results.data.length }, 'CSV loaded')
          resolve(results.data)
        },
        error: (error: Error) => {
          logger.error({ error }, 'Error parsing CSV')
          reject(error)
        },
      })
    })
  } catch (error) {
    logger.error({ error }, 'Error loading CSV file')
    return []
  }
}

// Process CSV data into SleepData format for the selected year
export const processCSVData = (
  csvData: OuraCSVRow[],
  year: number,
): SleepData[] => {
  // For 2022, start from August (when Oura ring was purchased)
  const startMonth = year === 2022 ? 7 : 0
  const startDate = new Date(year, startMonth, 1)
  const endDate = new Date(year, 11, 31)
  const totalDays =
    Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
    ) + 1

  // Initialize all days with null values
  const days: SleepData[] = Array.from({ length: totalDays }, (_, i) => {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)
    return {
      date,
      hoursSlept: null,
      sleepScore: null,
      deepSleep: null,
      remSleep: null,
      totalSleep: null,
      restingHeartRate: null,
      readinessScore: null,
      activityScore: null,
      steps: null,
      hrv: null,
      sleepDebt: null,
      sleepEfficiency: null,
    }
  })

  const today = new Date()
  today.setHours(23, 59, 59, 999)

  // Fill in with actual data where available
  for (const row of csvData) {
    try {
      const rowDate = parseISO(row.day)
      // Shift date by +1 year for display
      const displayDate = new Date(rowDate)
      displayDate.setFullYear(rowDate.getFullYear() + 1)

      // Skip if not in selected year or is a future date
      if (displayDate.getFullYear() !== year || displayDate > today) {
        continue
      }

      const dayIndex = differenceInCalendarDays(displayDate, startDate)
      if (dayIndex >= 0 && dayIndex < days.length) {
        days[dayIndex] = {
          date: displayDate,
          hoursSlept: row.total_sleep_hours ?? null,
          sleepScore: row.daily_sleep_score ?? null,
          deepSleep: row.deep_pct ?? null,
          remSleep: row.rem_pct ?? null,
          totalSleep: row.total_sleep_hours ?? null,
          restingHeartRate: row.sleep_average_heart_rate ?? null,
          readinessScore: row.readiness_score ?? null,
          activityScore: row.activity_score ?? null,
          steps: row.activity_steps ?? null,
          hrv: row.sleep_average_hrv ?? null,
          sleepDebt: row.sleep_debt_hours ?? null,
          sleepEfficiency: row.sleep_efficiency ?? null,
        }
      }
    } catch (error) {
      logger.error({ error }, 'Error processing CSV row')
    }
  }

  return days
}

export const generateYearData = (selectedYear: number): SleepData[] => {
  // For 2022, start from August
  const startMonth = selectedYear === 2022 ? 7 : 0
  const startDate = new Date(selectedYear, startMonth, 1)
  const endDate = new Date(selectedYear, 11, 31)
  const totalDays =
    Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
    ) + 1

  // For current year, don't generate dates past today
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  const isCurrentYear = selectedYear === currentYear()

  const days: SleepData[] = []
  for (let i = 0; i < totalDays; i++) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)

    // Stop generating future dates for current year
    if (isCurrentYear && date > today) {
      break
    }

    days.push({
      date,
      hoursSlept: null,
      sleepScore: null,
      deepSleep: null,
      remSleep: null,
      totalSleep: null,
      restingHeartRate: null,
      readinessScore: null,
      activityScore: null,
      steps: null,
      hrv: null,
      sleepDebt: null,
      sleepEfficiency: null,
    })
  }
  return days
}

export const calculateTotalSleep = (sleepData: SleepData[]): string => {
  let totalHours = 0

  for (const day of sleepData) {
    if (day.hoursSlept !== null && day.hoursSlept !== undefined) {
      // Convert to hours if needed
      const hours =
        day.hoursSlept > 100 ? day.hoursSlept / 3600 : day.hoursSlept
      if (!isNaN(hours)) {
        totalHours += hours
      }
    }
  }

  // Round to the nearest whole number
  return Math.round(totalHours).toString()
}
